
import { useEffect,useState, type JSX } from "react";
import { Controller, useForm } from "react-hook-form"
import { RoleLevel, roles, type User, type rolesList  } from "../../types/Users";
import { FloatingInputBootStrap, FloatingPrefixInputBootStrap } from "../Common/FloatingInputBootstrap";
import type { ResponseError } from "../../types/CommonReponse";
import { useUpdateUserMutation } from "../../stores/OkOkStore";
import { Navigate } from "react-router";
import { useTrads } from "../../contexts/trads/useTrads";

import AttachmentsSelector from "../Common/AttachmentsSelector.tsx";
import { getPostCodeMunicipalities } from "../../tools/geo.tsx";
import { CivSetter } from "../Common/CivSetter.tsx";
import {validateNumNational, validateDateNaissance, validateIBAN, validateTelephone} from "../../tools/validations.ts"
interface Props  {
    userParam: Partial<User> & Pick<User,'id'>;
   
};


export function  UserAdminEditForm ({userParam}: Props): JSX.Element {
    const [updateProfil, result] = useUpdateUserMutation();
    const {translate}=useTrads();
    const [communeSuggestions,setCommuneSuggestions]=useState<string[]>([]);


    const { register,control, handleSubmit,watch,setValue,setError,formState: { errors } } = useForm({
        defaultValues:userParam,
       // shouldUseNativeValidation: true,
        mode:'onBlur',
        
    });

    const isRole=function(role:rolesList):boolean{
        return RoleLevel[role] == RoleLevel[userParam.rangacces??"UV"];
    };

    const postcode=watch('code_postal');

    useEffect(()=>{
        if(result.error && 'data' in result.error) {
            const server_error:ResponseError=result.error as ResponseError;
            const errors =server_error.data.error;
            if(errors){
                for (const i in errors) {
                        setError(i, {type:'validate',message: translate(errors[i])});
                }
                result.reset();
            }
        }
    },[setError,result,translate]);

    useEffect(()=>{
        if(postcode?.length==4){
            getPostCodeMunicipalities(postcode,(suggestions:string[])=>{
                if(suggestions.length==1){
                    setValue("commune",suggestions[0]);
                }
                setCommuneSuggestions(suggestions);
            });
        }else{
            setValue("commune","");
        }
    },[postcode,setValue,translate]

    );

    

    return (

        result.isSuccess ? <Navigate to={`/user/${result.data.id.toString()}`} state={{modified:"true"}}></Navigate> :(
        result.error ? <p className="p-3 mb-2 bg-danger text-white">{translate('page_load_error')}</p>:
        result.isLoading ? <p className="p-3 mb-2 bg-info text-white">{translate('page_loading')}</p>:
            <form className="row" onSubmit={(event) => void handleSubmit(updateProfil)(event)}>
                <div className="col-12  mb-3">

                    <CivSetter 
                        name='civilite' 
                        label={translate('profil_civilite')}
                        registerOutput={register("civilite", {
                            required: translate('profil_error_civilite_requis'),
                        })}
                        errors={errors}
                        rest={{"autocomplete" : "honorific-prefix"}}
                    />

                </div>
                <div className="col-12  mb-3">

                    <FloatingInputBootStrap 
                        name='nom' 
                        label={translate('profil_nom')}
                        type='text' 
                        registerOutput={register("nom", {
                            required: translate('profil_error_nom_requis'),
                            minLength: {
                                value:2,
                                message: translate('profil_error_nom_court')
                            }
                        })}
                        errors={errors}
                        rest={{"autoComplete":"family-name"}}
                    />
                </div>
                <div className="col-12  mb-3">

                    <FloatingInputBootStrap 
                        name='prenom' 
                        label={translate('profil_prenom')}
                        type='text' 
                        registerOutput={register("prenom", {
                            required: translate('profil_error_prenom_requis'),
                            minLength: {
                                value:2,
                                message: translate('profil_error_prenom_court')
                            }
                        })}
                        errors={errors}
                        rest={{"autoComplete":"given-name"}}
                    />
                </div>
                {isRole(roles.Beneficiaires) || isRole(roles.PasValider)?
                <div className="col-12  mb-3">

                    <FloatingInputBootStrap 
                        name='date_de_naissance' 
                        label={translate('profil_date_naissance')} 
                        type='date' 
                        registerOutput={register("date_de_naissance", {
                            required: translate('profil_error_date_naissance_requis'),
                            validate: (value)=> validateDateNaissance(value,translate),
                        })}
                        errors={errors}
                        rest={{"autoComplete":"bday"}}
                    />
                </div>
                :null}
                <div className="col-12  mb-3">

                    <FloatingInputBootStrap 
                        name='adresse' 
                        label={translate('profil_adresse')}  
                        type='text' 
                        registerOutput={register("adresse", {
                            required: translate('profil_error_adresse_requis'),
                            minLength: {
                                value:2,
                                message: translate('profil_error_adresse_court')
                            }
                        })}
                        errors={errors}
                        rest={{"autoComplete":"street-address"}}
                    />

                </div>
                <div className="col-12  mb-3">

                    <FloatingInputBootStrap 
                        name='code_postal' 
                        label={translate('profil_code_postal')}
                        type='number' 
                        registerOutput={register("code_postal", {
                            required: translate('profil_error_code_postal_requis'),
                            minLength: {
                                value:4,
                                message: translate('profil_error_code_postal')
                            },
                            maxLength:{
                                value:4,
                                message: translate('profil_error_code_postal')  
                            }

                        })}
                        errors={errors}
                        rest={{"autoComplete":"postal-code"}}
                    />

                </div>
                
                <div className="col-12  mb-3">

                    <FloatingInputBootStrap 
                        name='commune' 
                        label={translate('profil_commune')}
                        type='text' 
                        registerOutput={register("commune", {
                            required:  translate('profil_error_commune_requis'),
                            minLength: {
                                value:2,
                                message: translate('profil_error_commune')
                            }
                        })}
                        errors={errors}
                        rest={{
                            "autoComplete":"address-level2",
                            "list":"commune-suggestions"
                        }}
                    />
                </div>
                

                <div className="col-12  mb-3">

                    <FloatingPrefixInputBootStrap
                        name='tel' 
                        label={translate('profil_telephone')}
                        type='tel' 
                        registerOutput={register("tel", {
                            required:  translate('profil_error_telephone_requis'),
                            validate: (value)=> validateTelephone(value??'',translate),
                            
                            
                        })}
                        errors={errors}
                        prefix="📞"
                        rest={{
                            "autoComplete":"tel",
                            "pattern": "0\\d{9,10}",
                            "placeholder": "0XXXXXXXXX"
                        }}
                        hint={translate("profil_tel_hint")}
                    />
                </div>

                {isRole(roles.Beneficiaires) || isRole(roles.PasValider)?
                <>
                <div className="col-12  mb-3">
                    <FloatingInputBootStrap 
                        name='titulaire_iban' 
                        label={translate('profil_titulaire_iban')}
                        type='text' 
                        registerOutput={register("titulaire_iban", {
                            required:  translate('profil_error_titulaire_iban_requis'),
                            minLength: {
                                value:2,
                                message: translate('profil_error_titulaire_iban')
                            }
                        })}
                        errors={errors}
                    />
                </div>
                <div className="col-12  mb-3">
                    <FloatingInputBootStrap 
                        name='iban' 
                        label={translate('profil_iban')} 
                        type='text' 
                        registerOutput={register("iban", {
                            required: translate('profil_error_iban_requis'),
                            validate: (value)=> validateIBAN(value??'',translate),
                            minLength: {
                                value:2,
                                message: translate('profil_error_iban')
                            }
                        })}
                        errors={errors}
                        rest={{
                            "placeholder":"BEXXXXXXXXXXXXXX",
                            "pattern": "BE\\d{14}"
                        }}
                        hint={translate("profil_iban_hint")}
                    />
                </div>

                </>
                :null}
                {isRole(roles.Beneficiaires) || isRole(roles.PasValider)?
                <div className="col-12  mb-3">

                    <FloatingInputBootStrap 
                        name='numero_national' 
                        label={translate('profil_num_national')} 
                        type='text' 
                        registerOutput={register("numero_national", {
                            required: translate('profil_error_num_national_requis'),
                            validate: (value)=>validateNumNational(value??'',translate),
                        })}
                        errors={errors}
                        hint={translate("profil_numero_national_hint")}
                    />


                </div>
                :null}


                {isRole(roles.PasValider)?
                <>
                <div className="col-12 mb3">
                    <p>{translate('profil_reconnaissance_handicap')}</p>
                    <Controller
                         control={control}
                         name="reconnaissances"
                         render={({ field }) => <AttachmentsSelector value={field.value} name={field.name} onBlur={field.onBlur} onChange={field.onChange} disabled={field.disabled} accept="image/png, image/jpeg, application/pdf" />}
                    />
                </div>
                <div className="col-12 mb3">
                    <p>{translate('profil_rib')}</p>
                    <Controller
                         control={control}
                         name="ribs"
                         render={({ field }) => <AttachmentsSelector value={field.value} name={field.name} onBlur={field.onBlur} onChange={field.onChange} disabled={field.disabled} accept="image/png, image/jpeg, application/pdf" />}
                    />
                </div>                
                <div className="col-12 mb3">
                    <p>{translate('profil_carte_identite')}</p>
                    <Controller
                         control={control}
                         name="carte_identites"
                         render={({ field }) => <AttachmentsSelector value={field.value} name={field.name} onBlur={field.onBlur} onChange={field.onChange} disabled={field.disabled} accept="image/png, image/jpeg, application/pdf" />}
                    />
                </div>   

                </>
                :null}

                <datalist id="commune-suggestions">
                    {communeSuggestions.map(e=>
                        <option key={e} value={e}></option>
                    )}
                </datalist>


                <div className="col-12  mb-3">
                    <button type="submit">{translate('form_submit')}</button>
                </div>
                    
            </form>
            
        )
    )
};

export default UserAdminEditForm;